import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, MapPin, AlertTriangle, Radar } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import {
  getAllGeoFences,
  createGeoFence,
  checkGeoFenceLocation,
  getGeoFenceViolations,
  GeoFence,
  GeoFenceViolation,
} from "../../../service/securityService";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const GeoFenceTab: React.FC = () => {
  const [fences, setFences] = useState<GeoFence[]>([]);
  const [violations, setViolations] = useState<GeoFenceViolation[]>([]);
  const [loading, setLoading] = useState(true);
  const [fenceModal, setFenceModal] = useState(false);
  const [testModal, setTestModal] = useState(false);
  const [fenceForm, setFenceForm] = useState({
    fenceName: "",
    fenceType: "Restricted Zone",
    alertLevel: "High",
    lat: "",
    lng: "",
    radius: "200",
  });
  const [testForm, setTestForm] = useState({
    userId: "",
    lat: "",
    lng: "",
  });

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [fRes, vRes] = await Promise.all([
        getAllGeoFences({ page: 1, limit: 100 }),
        getGeoFenceViolations({ page: 1, limit: 100 }),
      ]);
      setFences(fRes.data || []);
      setViolations(vRes.data || []);
    } catch (e: any) {
      console.error(e);
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to load geo-fences",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const submitFence = async (e: React.FormEvent) => {
    e.preventDefault();
    const lat = parseFloat(fenceForm.lat);
    const lng = parseFloat(fenceForm.lng);
    const radius = parseFloat(fenceForm.radius);
    if (!fenceForm.fenceName.trim() || Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("Name, latitude, and longitude are required");
      return;
    }
    try {
      await createGeoFence({
        fenceName: fenceForm.fenceName.trim(),
        fenceType: fenceForm.fenceType,
        alertLevel: fenceForm.alertLevel,
        isActive: true,
        center: { lat, lng },
        radius: Number.isNaN(radius) ? 100 : radius,
      });
      toast.success("Geo-fence created");
      setFenceModal(false);
      setFenceForm({
        fenceName: "",
        fenceType: "Restricted Zone",
        alertLevel: "High",
        lat: "",
        lng: "",
        radius: "200",
      });
      loadAll();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.resMessage ||
          "Create failed",
      );
    }
  };

  const submitTest = async (e: React.FormEvent) => {
    e.preventDefault();
    const lat = parseFloat(testForm.lat);
    const lng = parseFloat(testForm.lng);
    if (!testForm.userId.trim() || Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("User ID and coordinates required");
      return;
    }
    try {
      const r = await checkGeoFenceLocation({
        userId: testForm.userId.trim(),
        location: { lat, lng },
      });
      if (r.hasViolations) {
        toast.warning(
          `Violation recorded: ${r.violations.length} alert(s). Check Security incidents for High/Critical fences.`,
        );
      } else {
        toast.success("No violation for active fence rules at this point");
      }
      setTestModal(false);
      loadAll();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.resMessage ||
          "Check failed",
      );
    }
  };

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: "#fff",
        foregroundColor: "#2a2a2a",
        headerTextColor: "#2a2a2a",
        headerBackgroundColor: "#E9F5FF",
        oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
        headerColumnResizeHandleColor: "rgb(126, 46, 132)",
        fontSize: "0.75rem",
        headerFontWeight: "bold",
      }),
    [],
  );

  const fenceCols = useMemo<ColDef[]>(
    () => [
      { field: "fenceName", headerName: "Name", flex: 1.2, minWidth: 120 },
      { field: "fenceType", headerName: "Type", flex: 1, minWidth: 110 },
      { field: "alertLevel", headerName: "Alert", flex: 0.7, minWidth: 80 },
      {
        field: "isActive",
        headerName: "Active",
        flex: 0.6,
        minWidth: 70,
        valueGetter: (p) => (p.data?.isActive ? "Yes" : "No"),
      },
      {
        field: "center",
        headerName: "Center",
        flex: 1.2,
        minWidth: 140,
        valueGetter: (p) => {
          const c = p.data?.center;
          return c ? `${c.lat?.toFixed?.(5) ?? c.lat}, ${c.lng?.toFixed?.(5) ?? c.lng}` : "—";
        },
      },
      {
        field: "radius",
        headerName: "Radius (m)",
        flex: 0.8,
        minWidth: 90,
        valueGetter: (p) => p.data?.radius ?? "—",
      },
    ],
    [],
  );

  const violCols = useMemo<ColDef[]>(
    () => [
      { field: "fenceName", headerName: "Fence", flex: 1, minWidth: 120 },
      { field: "violationType", headerName: "Type", flex: 1, minWidth: 100 },
      {
        field: "timestamp",
        headerName: "When",
        flex: 1.1,
        minWidth: 150,
        valueGetter: (p) =>
          p.data?.timestamp
            ? new Date(p.data.timestamp).toLocaleString()
            : "—",
      },
      { field: "userId", headerName: "User", flex: 1, minWidth: 100 },
      {
        field: "location",
        headerName: "Coords",
        flex: 1,
        minWidth: 120,
        valueGetter: (p) => {
          const l = p.data?.location;
          return l ? `${l.lat}, ${l.lng}` : "—";
        },
      },
      { field: "status", headerName: "Status", flex: 0.7, minWidth: 80 },
    ],
    [],
  );

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-between items-center gap-3 p-4 border-b">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Radar className="w-5 h-5 text-[#0fa9a6]" />
            Campus geo-fencing
          </h3>
          <p className="text-sm text-gray-600 max-w-2xl">
            Define circular zones; mobile or dispatch can POST{" "}
            <code className="text-xs bg-gray-100 px-1 rounded">
              /api/security/geofences/check-location
            </code>{" "}
            with userId and lat/lng. High/Critical fences also create Security
            incidents.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTestModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-[#0fa9a6] text-[#0fa9a6] rounded-full hover:bg-[#EAFBFA]"
          >
            <MapPin className="w-4 h-4" />
            Test location
          </button>
          <button
            type="button"
            onClick={() => setFenceModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600"
          >
            <Plus className="w-5 h-5" />
            New fence
          </button>
        </div>
      </div>

      <div className="p-4 space-y-8">
        {loading ? (
          <div className="flex justify-center p-12">
            <SpinLoader />
          </div>
        ) : (
          <>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Active fences</h4>
              {fences.length === 0 ? (
                <p className="text-gray-500 text-sm">No fences yet.</p>
              ) : (
                <div className="ag-theme-alpine w-full min-h-[200px]">
                  <AgGridReact
                    rowData={fences}
                    columnDefs={fenceCols}
                    theme={gridTheme}
                    headerHeight={52}
                    rowHeight={44}
                    domLayout="autoHeight"
                  />
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Recent violations
              </h4>
              {violations.length === 0 ? (
                <p className="text-gray-500 text-sm">No violations logged.</p>
              ) : (
                <div className="ag-theme-alpine w-full min-h-[200px]">
                  <AgGridReact
                    rowData={violations}
                    columnDefs={violCols}
                    theme={gridTheme}
                    headerHeight={52}
                    rowHeight={44}
                    domLayout="autoHeight"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={fenceModal}
        onClose={() => setFenceModal(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg"
      >
        <form id="gfForm" onSubmit={submitFence} className="p-6 space-y-3">
          <h2 className="text-lg font-semibold border-b pb-2">New geo-fence</h2>
          <div>
            <label className="text-sm font-medium">Name *</label>
            <input
              className="mt-1 w-full border rounded-md p-2"
              value={fenceForm.fenceName}
              onChange={(e) =>
                setFenceForm({ ...fenceForm, fenceName: e.target.value })
              }
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium">Type</label>
              <select
                className="mt-1 w-full border rounded-md p-2"
                value={fenceForm.fenceType}
                onChange={(e) =>
                  setFenceForm({ ...fenceForm, fenceType: e.target.value })
                }
              >
                <option value="Restricted Zone">Restricted Zone</option>
                <option value="Allowed Zone">Allowed Zone</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Alert level</label>
              <select
                className="mt-1 w-full border rounded-md p-2"
                value={fenceForm.alertLevel}
                onChange={(e) =>
                  setFenceForm({ ...fenceForm, alertLevel: e.target.value })
                }
              >
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium">Latitude *</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={fenceForm.lat}
                onChange={(e) => setFenceForm({ ...fenceForm, lat: e.target.value })}
                placeholder="e.g. 12.97"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Longitude *</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={fenceForm.lng}
                onChange={(e) => setFenceForm({ ...fenceForm, lng: e.target.value })}
                placeholder="e.g. 77.59"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Radius (meters)</label>
            <input
              type="number"
              min={10}
              className="mt-1 w-full border rounded-md p-2"
              value={fenceForm.radius}
              onChange={(e) =>
                setFenceForm({ ...fenceForm, radius: e.target.value })
              }
            />
          </div>
        </form>
        <div className="flex justify-end gap-2 p-3 bg-[#5A7192] rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full"
            onClick={() => setFenceModal(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="gfForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full"
          >
            Save
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={testModal}
        onClose={() => setTestModal(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg"
      >
        <form id="testGf" onSubmit={submitTest} className="p-6 space-y-3">
          <h2 className="text-lg font-semibold border-b pb-2">Test GPS position</h2>
          <p className="text-xs text-gray-600">
            Uses the same rules as the mobile integration: restricted zones and
            per-user lists on the fence document.
          </p>
          <div>
            <label className="text-sm font-medium">User ID *</label>
            <input
              className="mt-1 w-full border rounded-md p-2"
              value={testForm.userId}
              onChange={(e) =>
                setTestForm({ ...testForm, userId: e.target.value })
              }
              placeholder="Staff user id string"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium">Latitude *</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={testForm.lat}
                onChange={(e) =>
                  setTestForm({ ...testForm, lat: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Longitude *</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={testForm.lng}
                onChange={(e) =>
                  setTestForm({ ...testForm, lng: e.target.value })
                }
                required
              />
            </div>
          </div>
        </form>
        <div className="flex justify-end gap-2 p-3 bg-[#5A7192] rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full"
            onClick={() => setTestModal(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="testGf"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full"
          >
            Run check
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GeoFenceTab;
